import moment from 'moment';

/**
 * Monta o nome do arquivo PDF de um relatório a partir de um padrão configurável
 * por empresa (`companyInfo.reportFilenamePattern`).
 *
 * Tokens disponíveis:
 *   {code}        código do comissionamento          ex.: RTCB
 *   {location}    local do subject (alias: {site})   ex.: SGRUTB09
 *   {sequential}  sequencial com 4 dígitos            ex.: 0010
 *   {origin}      propriedade "origin" do subject     ex.: MSB-4
 *   {destiny}     propriedade "destiny" do subject    ex.: DSB-CH-4A
 *                 (alias: {destination})
 *   {subject}     nome do subject
 *   {token}       token da ordem de serviço
 *   {date}        data de execução (YYYY-MM-DD)
 *
 * Trechos entre colchetes são opcionais: somem inteiros quando qualquer token
 * dentro deles estiver vazio. Isso mantém o nome limpo para ativos que não têm
 * origem/destino (só interconexões têm).
 *
 * Exemplo — padrão `{code}-{location}-{sequential}[ ({origin} to {destiny})]`:
 *   interconexão -> "RTCB-SGRUTB09-0010 (MSB-4 to DSB-CH-4A).pdf"
 *   ativo        -> "RTCB-SGRUTB09-0010.pdf"
 */

const INVALID_FILENAME_CHARS = /[\\/:*?"<>|]/g;
const MAX_FILENAME_LENGTH = 180;

const getSubjectProperty = (subject, key) => {
  const properties = subject?.properties;
  const value = Array.isArray(properties)
    ? properties.find((property) => property?.key === key)?.value
    : properties?.[key];

  return typeof value === 'string' ? value : '';
};

const buildTokens = (report) => {
  const location = report?.subject?.location?.name ?? '';
  const destiny = getSubjectProperty(report?.subject, 'destiny');
  const sequential =
    report?.sequential === undefined || report?.sequential === null
      ? ''
      : report.sequential.toString().padStart(4, '0');

  return {
    code: report?.code ?? '',
    location,
    site: location,
    sequential,
    origin: getSubjectProperty(report?.subject, 'origin'),
    destiny,
    destination: destiny,
    subject: report?.subject?.name ?? '',
    token: report?.token ?? '',
    date: report?.createdAt ? moment(report.createdAt).format('YYYY-MM-DD') : '',
  };
};

/**
 * Substitui os tokens de um trecho do padrão.
 * @returns {{ text: string, hasEmptyToken: boolean }} `hasEmptyToken` sinaliza
 * token vazio ou desconhecido — usado para descartar grupos opcionais.
 */
const replaceTokens = (text, tokens) => {
  let hasEmptyToken = false;

  const replaced = text.replace(/\{(\w+)\}/g, (_match, key) => {
    const value = tokens[key.toLowerCase()];
    if (!value) {
      hasEmptyToken = true;
      return '';
    }
    return value;
  });

  return { text: replaced, hasEmptyToken };
};

const sanitize = (name) =>
  name.replace(INVALID_FILENAME_CHARS, '-').replace(/\s+/g, ' ').trim().slice(0, MAX_FILENAME_LENGTH);

const buildFromPattern = (report, pattern) => {
  const tokens = buildTokens(report);

  const withOptionalGroups = pattern.replace(/\[([^[\]]*)\]/g, (_match, group) => {
    const { text, hasEmptyToken } = replaceTokens(group, tokens);
    return hasEmptyToken ? '' : text;
  });

  return sanitize(replaceTokens(withOptionalGroups, tokens).text);
};

const buildLegacyFilename = (report) => {
  const tokens = buildTokens(report);
  return sanitize([tokens.code, tokens.location, tokens.sequential].filter(Boolean).join('-'));
};

/**
 * Nome final do arquivo do relatório.
 * Sem padrão configurado, mantém o comportamento antigo: usa o `report.filename`
 * gravado na tarefa e cai no `{code}-{location}-{sequential}` quando não houver.
 *
 * @param {Object} report - documento da commissioningtask
 * @param {string} [pattern] - `companyInfo.reportFilenamePattern`
 * @returns {string} nome do arquivo com extensão `.pdf`
 */
export const getReportFilename = (report, pattern) => {
  const fromPattern = pattern ? buildFromPattern(report, pattern) : '';
  const name =
    fromPattern || sanitize(report?.report?.filename ?? '') || buildLegacyFilename(report) || 'report';

  return name.toLowerCase().endsWith('.pdf') ? name : `${name}.pdf`;
};

export default getReportFilename;
